/**
 * Fuse.js Search Module - Альтернативный модуль сортировки через Fuse.js
 */

(function(window) {
    'use strict';

    const FuseSearch = {};

    /**
     * Helper to escape HTML characters
     */
    const escapeHtml = (str) => {
        if (!str) return '';
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    };

    /**
     * Rank items using Fuse.js only (without uFuzzy preprocessing).
     */
    FuseSearch.rankItems = function(query, items, options = {}) {
        const debugMode = typeof window !== 'undefined' && window.location && window.location.search.includes('debug=1') ? '1' : '0';
        
        if (!window.Fuse || !query || !items || items.length === 0) {
            return (items || []).map(item => ({ item, score: 0 }));
        }

        const keys = options.keys || ['fullPath', 'name', 'path', 'n', 'p'];
        const threshold = options.threshold !== undefined ? options.threshold : 0.3;
        
        // Обрабатываем запрос: убираем спецсимволы, но СОХРАНЯЕМ пробелы для поиска отдельных слов
        // Это позволит находить "слово1...слово2", даже если между ними есть другой текст
        const normalizedQuery = String(query)
            .replace(/[\.\-\/]/g, ' ') // Заменяем точки, тире и слеши на пробелы
            .trim()
            .replace(/\s+/g, ' ');    // Схлопываем множественные пробелы в один
        
        const normalizeString = (str) => {
            if (typeof str !== 'string') return str || '';
            return str.replace(/[\.\-\/\s]/g, '');
        };
        
        const itemsWithNormalized = items.map((item, idx) => {
            const normalizedItem = { ...item, _originalIndex: idx };
            for (const key of keys) {
                const value = item[key];
                if (value !== undefined && value !== null) {
                    normalizedItem[`_norm_${key}`] = normalizeString(String(value));
                }
            }
            return normalizedItem;
        });

        const fuseOptions = {
            keys: keys.map(key => ({
                name: `_norm_${key}`,
                weight: (options.weights && options.weights[key]) || 1.0
            })),
            includeScore: true,
            includeMatches: !!options.includeMatches,
            threshold: threshold,
            ignoreLocation: true,
            minMatchCharLength: 1,
            useExtendedSearch: true // Включаем расширенный поиск для поддержки логики "слово1 И слово2"
        };

        try {
            const fuse = new window.Fuse(itemsWithNormalized, fuseOptions);
            const results = fuse.search(normalizedQuery);
            
            const maxScore = 1000;
            const matchedItems = results.map((result, rank) => {
                const originalItem = items[result.item._originalIndex];
                const fuseScore = result.score || 1.0;
                const finalScore = Math.max(maxScore - (fuseScore * maxScore), maxScore - rank);
                
                const resultData = { item: originalItem, score: finalScore };
                if (options.includeMatches && result.matches) {
                    resultData.matches = result.matches;
                }
                return resultData;
            });

            const matchedIndicesSet = new Set(results.map(r => r.item._originalIndex));
            const unmatchedItems = items
                .map((item, originalIdx) => ({ item, originalIdx }))
                .filter(obj => !matchedIndicesSet.has(obj.originalIdx))
                .map(obj => ({ item: obj.item, score: 0 }));

            return [...matchedItems, ...unmatchedItems];
        } catch (e) {
            console.error('[FuseSearch.rankItems] Fuse.js error:', e);
            return items.map(item => ({ item, score: 0 }));
        }
    };

    /**
     * Highlight text using Fuse.js matches
     * Returns HTML string with <b> tags for matches
     */
    FuseSearch.highlightMatches = function(text, query, matches, key = null) {
        if (!text) return '';
        const debugMode = typeof window !== 'undefined' && window.location && window.location.search.includes('debug=1') ? '1' : '0';
        
        if (!query) return escapeHtml(text);
        
        // Разбиваем запрос на слова (минимум 2 символа)
        const queryWords = query.trim().split(/[\s\.\-\/_]+/).filter(w => w.length >= 2);
        
        if (queryWords.length === 0) {
            // Пробуем подсветить весь запрос если он короткий
            const result = FuseSearch._highlightFuzzy(text, query);
            return result === text ? escapeHtml(text) : result;
        }

        let currentHtml = text;
        let anyMatch = false;
        
        for (const word of queryWords) {
            const nextHtml = FuseSearch._highlightFuzzy(currentHtml, word);
            if (nextHtml !== currentHtml) {
                currentHtml = nextHtml;
                anyMatch = true;
            }
        }
        
        // Если ни одно слово не совпало, возвращаем просто экранированный текст
        if (!anyMatch) return escapeHtml(text);
        
        // Если были совпадения, currentHtml уже содержит <b> и экранированные части
        return currentHtml;
    };

    /**
     * Fuzzy подсветка одного слова в тексте (с поддержкой уже имеющихся <b>)
     */
    FuseSearch._highlightFuzzy = function(text, queryWord) {
        if (!text || !queryWord) return text;
        
        const debugMode = typeof window !== 'undefined' && window.location && window.location.search.includes('debug=1') ? '1' : '0';
        const queryLower = queryWord.toLowerCase();
        const normalizedQuery = queryLower.replace(/[\.\-\/\s]/g, '');
        if (!normalizedQuery || normalizedQuery.length < 2) return text;

        if (debugMode === '1') {
            console.log(`[FuseSearch._highlightFuzzy] Searching for "${queryWord}" (norm: "${normalizedQuery}") in text: "${text.substring(0, 50)}..."`);
        }

        // Вспомогательная функция для расчета похожести (Levenshtein distance)
        const getSimilarity = (s1, s2) => {
            if (s1 === s2) return 1.0;
            const len1 = s1.length;
            const len2 = s2.length;
            if (len1 === 0 || len2 === 0) return 0.0;
            
            const matrix = Array.from({ length: len1 + 1 }, () => Array(len2 + 1).fill(0));
            for (let i = 0; i <= len1; i++) matrix[i][0] = i;
            for (let j = 0; j <= len2; j++) matrix[0][j] = j;
            
            for (let i = 1; i <= len1; i++) {
                for (let j = 1; j <= len2; j++) {
                    const cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
                    matrix[i][j] = Math.min(
                        matrix[i - 1][j] + 1,      // deletion
                        matrix[i][j - 1] + 1,      // insertion
                        matrix[i - 1][j - 1] + cost // substitution
                    );
                }
            }
            return 1.0 - (matrix[len1][len2] / Math.max(len1, len2));
        };

        // Токены: <b>...</b>, слова (Unicode), разделители, спецсимволы
        const tokenRegex = /<b>.*?<\/b>|[\p{L}\p{N}]+|[ \.\-\/_]+|[^\p{L}\p{N} \.\-\/_]+/gu;
        const tokens = text.match(tokenRegex) || [];
        
        if (debugMode === '1' && tokens.length < 5) {
            console.log(`[FuseSearch._highlightFuzzy] Tokens:`, tokens);
        }

        let resultHtml = '';
        let hasMatched = false;

        for (let token of tokens) {
            // Если это уже тег или разделитель - добавляем как есть
            if (token.startsWith('<b>') || /^[ \.\-\/_]+$/.test(token)) {
                resultHtml += token;
                continue;
            }

            const normalizedToken = token.toLowerCase().replace(/[\.\-\/\s]/g, '');
            if (!normalizedToken || normalizedToken.length < 2) {
                resultHtml += escapeHtml(token);
                continue;
            }

            let isMatch = false;
            let similarity = 0;

            // 1. Прямое вхождение
            if (normalizedToken.includes(normalizedQuery) || normalizedQuery.includes(normalizedToken)) {
                isMatch = true;
                similarity = 1.0;
            } else if (normalizedToken.length >= 3 && normalizedQuery.length >= 3) {
                // 2. Нечеткое сравнение (Levenshtein)
                similarity = getSimilarity(normalizedToken, normalizedQuery);
                if (similarity >= 0.6) { // Снизил порог до 0.6 для большей гибкости
                    isMatch = true;
                }
            }

            if (isMatch) {
                resultHtml += `<b>${escapeHtml(token)}</b>`;
                hasMatched = true;
                if (debugMode === '1') {
                    console.log(`[FuseSearch._highlightFuzzy] ✅ Match found: "${token}" similarity to "${queryWord}" is ${similarity.toFixed(2)}`);
                }
            } else {
                resultHtml += escapeHtml(token);
                // Только для очень похожих, но не прошедших порог, пишем в лог
                if (debugMode === '1' && similarity > 0.4) {
                    console.log(`[FuseSearch._highlightFuzzy] ❌ No match: "${token}" similarity to "${queryWord}" is ${similarity.toFixed(2)}`);
                }
            }
        }

        if (debugMode === '1' && hasMatched) {
            console.log(`[FuseSearch._highlightFuzzy] ✅ Result for "${queryWord}":`, resultHtml.substring(0, 100));
        }

        return hasMatched ? resultHtml : text; 
    };

    window.FuseSearch = FuseSearch;
    window.fuseHighlightMatches = FuseSearch.highlightMatches;

})(window);

export const fuseSearch = window.FuseSearch;
